import { useState, useEffect } from 'react'
import { ConversationHistoryResponse } from '../types/conversationHistory'
import { ConversationHistoryApiService } from '../services/conversationHistoryApiService'

interface UseConversationHistoryReturn {
  history: ConversationHistoryResponse | null
  isLoading: boolean
  error: string | null
  loadHistory: (phone: string) => Promise<ConversationHistoryResponse | null>
  clearHistory: () => void
  clearError: () => void
}

export const useConversationHistory = (apiBaseUrl: string): UseConversationHistoryReturn => {
  const [history, setHistory] = useState<ConversationHistoryResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apiService = new ConversationHistoryApiService(apiBaseUrl)

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [error])

  const loadHistory = async (phone: string): Promise<ConversationHistoryResponse | null> => {
    if (!phone.trim()) {
      setError('Número de teléfono requerido')
      return null
    }

    setIsLoading(true)
    setError(null)
    setHistory(null)

    try {
      const result = await apiService.loadConversationHistory({
        whatsappPhone: phone,
        limit: 100,
        offset: 0
      })

      setHistory(result)
      
      if (result.messages.length === 0) {
        setError('No se encontró historial para este número')
      }
      
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido'
      
      if (errorMessage.includes('404')) {
        setError(`Endpoint no encontrado. Verifica que el servidor esté ejecutándose en ${apiBaseUrl}`)
      } else if (errorMessage.includes('Failed to fetch')) {
        setError(`No se puede conectar al servidor. Verifica la URL: ${apiBaseUrl}`)
      } else {
        setError(`Error al cargar historial: ${errorMessage}`)
      }
      
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const clearHistory = () => {
    setHistory(null)
    setError(null)
  }

  const clearError = () => {
    setError(null)
  }

  return {
    history,
    isLoading,
    error,
    loadHistory,
    clearHistory,
    clearError
  }
}
